import { ReactNode, useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { NotificationBell } from 'notification-kit/client/index.js';
import type { Page } from '../../App';
import { AssistantChat } from 'website-chat-assistant/client/AssistantChat';
import { api } from '../../api/client';

const LIBELLES_ASSISTANT: Record<string, string> = {
  accueil: 'Accueil', budget: 'Budget', salaires: 'Salaires', rh: 'RH',
  coutateliers: 'Coûts ateliers', charges: 'Charges', scenarios: 'Scénarios',
  revenus: 'Revenus', mesures: 'Mesures', loyers: 'Loyers', clients: 'Clients',
  contrats: 'Contrats', prestations: 'Prestations',
};

const PAGE_TITLES: Record<Page, string> = {
  accueil: 'Accueil',
  budget: 'Budget',
  salaires: 'Salaires',
  rh: 'RH — répartition du temps',
  coutateliers: 'Coûts ateliers',
  charges: 'Charges',
  scenarios: 'Scénarios',
  revenus: 'Revenus',
  mesures: 'Mesures',
  loyers: 'Loyers',
  clients: 'Clients',
  contrats: 'Contrats',
  prestations: 'Prestations',
  admin: 'Admin',
  profil: 'Profil',
  notifications: 'Notifications',
};

interface AppShellProps {
  children: ReactNode;
  currentPage: Page;
  onNavigate: (page: Page) => void;
}

export function AppShell({ children, currentPage, onNavigate }: AppShellProps) {
  const { user, logout, impersonatedBy, stopImpersonating, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  }, [collapsed]);

  const navItem = (page: Page, label: string) => (
    <a
      className={`nav-link${currentPage === page ? ' active' : ''}`}
      href={`#${page}`}
      onClick={(e) => { e.preventDefault(); onNavigate(page); }}
    >
      {label}
    </a>
  );

  return (
    <>
      <header className="topbar">
        <div className="topbar-left">
          <div className="brand">
            <span className="brand-mark" onClick={() => onNavigate('accueil')} style={{ cursor: 'pointer' }}>Budget</span>
            <span className="brand-sep" />
            <span className="brand-page">{PAGE_TITLES[currentPage]}</span>
          </div>
        </div>
        <div className="topbar-right">
          <NotificationBell onNavigate={(hash) => { window.location.hash = hash; }} />
          <button className="theme-btn" onClick={toggleTheme} title={theme === 'light' ? 'Mode sombre' : 'Mode clair'}>
            {theme === 'light' ? '☾' : '☀'}
          </button>
          <div
            className="user-tag"
            onClick={() => onNavigate('profil')}
            style={{ cursor: 'pointer' }}
            title="Mon profil"
          >
            <div className="user-initial">{user?.initials}</div>
            <span>{user?.name}</span>
          </div>
          {isAdmin && (
            <span
              className="badge badge-accent badge-clickable"
              onClick={() => onNavigate('admin')}
              title="Admin"
            >
              Admin
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={logout}>
            Déconnexion
          </button>
        </div>
      </header>

      <div className={`shell ${collapsed ? 'shell--collapsed' : ''}`}>
        <aside className="sidebar">
          <button className="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? '›' : '‹ Replier'}
          </button>

          {!collapsed && (
            <nav className="sidebar-nav">
              <div className="nav-section">
                <div className="nav-section-title"><span>Vues</span></div>
                {navItem('accueil', 'Accueil')}
                {navItem('budget', 'Budget')}
                {navItem('revenus', 'Revenus')}
                {navItem('mesures', 'Mesures')}
                {navItem('salaires', 'Salaires')}
                {navItem('rh', 'RH')}
                {navItem('coutateliers', 'Coûts ateliers')}
                {navItem('loyers', 'Loyers')}
                {navItem('charges', 'Charges')}
              </div>
              <div className="nav-section">
                <div className="nav-section-title"><span>Référentiels</span></div>
                {navItem('clients', 'Clients')}
                {navItem('contrats', 'Contrats')}
                {navItem('prestations', 'Prestations')}
                {navItem('scenarios', 'Scénarios')}
              </div>
            </nav>
          )}
        </aside>

        <main className="content">
          {impersonatedBy && (
            <div className="impersonation-banner">
              <span>Connecté·e en tant que <strong>{user?.name}</strong> — par {impersonatedBy.name}</span>
              <button className="btn btn-sm" onClick={() => { stopImpersonating(); window.location.reload(); }}>
                Arrêter
              </button>
            </div>
          )}
          <div className="page">{children}</div>
          <AssistantChat page={currentPage} pageLisible={LIBELLES_ASSISTANT[currentPage] ?? currentPage} api={api} />
        </main>
      </div>
    </>
  );
}
